import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Video, Send, Loader2, CheckCircle2, Image as ImageIcon, Film, Mic, MicOff } from "lucide-react";
import { CommissioningProtocol } from "@/components/CommissioningProtocol";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/installer-upload`;

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

function MicRecorder({ onTranscript }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const { token } = useParams();

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mime = "audio/webm";
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && !MediaRecorder.isTypeSupported(mime)) {
        if (MediaRecorder.isTypeSupported("audio/mp4")) mime = "audio/mp4";
        else mime = "";
      }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1000) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, `recording.${mr.mimeType.includes("mp4") ? "m4a" : "webm"}`);
          const res = await axios.post(`${API_BASE}/${token}/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (res.data?.text) onTranscript(res.data.text);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Transkription fehlgeschlagen");
        } finally {
          setBusy(false);
        }
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Mikrofon-Zugriff verweigert");
    }
  };
  const stop = () => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={() => (recording ? stop() : start())}
      disabled={busy}
      data-testid="installer-msg-mic"
      className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 transition active:scale-95 ${
        busy ? "bg-zinc-700 text-zinc-200" : recording ? "bg-red-500 text-white animate-pulse" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
      }`}
      title={recording ? "Aufnahme stoppen" : "Diktat starten"}
    >
      {busy ? <Loader2 className="animate-spin" size={18} /> : recording ? <MicOff size={18} /> : <Mic size={18} />}
    </button>
  );
}

export default function InstallerUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [photoCategory, setPhotoCategory] = useState("Wärmepumpe (Innen)");
  const [photoDescription, setPhotoDescription] = useState("");
  const [videoCategory, setVideoCategory] = useState("Wärmepumpe (Innen)");
  const [videoDescription, setVideoDescription] = useState("");
  const [message, setMessage] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const reload = async () => {
    try {
      const r = await axios.get(`${API_BASE}/${token}`);
      setInfo(r.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Upload-Link unbekannt oder abgelaufen.");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [token]);

  const handlePhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", photoCategory);
      fd.append("description", photoDescription);
      await axios.post(`${API_BASE}/${token}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Foto „${photoCategory}" hochgeladen`);
      setPhotoDescription("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleVideo = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Video zu groß (max. 200 MB)");
      return;
    }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", videoCategory);
      fd.append("description", videoDescription);
      await axios.post(`${API_BASE}/${token}/videos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Video „${videoCategory}" hochgeladen`);
      setVideoDescription("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const sendMessage = async () => {
    const t = message.trim();
    if (!t) return;
    setSendingMsg(true);
    try {
      await axios.post(`${API_BASE}/${token}/messages`, { text: t });
      toast.success("Nachricht an NOVATUS gesendet");
      setMessage("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSendingMsg(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#18181B] border border-zinc-800 rounded-2xl p-6 text-center">
          <p className="text-zinc-300">{error}</p>
        </div>
      </div>
    );
  }

  const labelCls = "text-[12px] font-bold uppercase tracking-wider text-zinc-400";
  const inputCls = "h-11 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-zinc-50 focus:border-[#00E575] outline-none text-sm";

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <header className="border-b border-zinc-800 bg-[#0F0F11]">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between gap-3">
          <div>
            <div className="text-[12px] uppercase tracking-[0.18em] text-[#00E575] font-bold">NOVATUS Energie</div>
            <h1 className="font-display text-xl font-bold mt-0.5">Installation-Upload</h1>
          </div>
          <div className="text-right text-xs text-zinc-400 font-medium">
            <div>Projekt</div>
            <div className="text-zinc-200 font-mono">{info.project_number}</div>
            {info.city && <div>{info.zip} {info.city}</div>}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-[#00E575]/10 border border-[#00E575]/30 rounded-2xl p-4 text-sm text-zinc-200">
          Bitte lade nach Abschluss der Installation Fotos &amp; Videos der wichtigsten Komponenten hoch.
          Du kannst auch eine kurze Notiz hinterlassen — diktiert oder geschrieben.
          Nach Fertigstellung der Foto-Dokumentation fülle bitte das <b>Inbetriebnahme-Protokoll</b> aus und lasse den Kunden digital unterschreiben.
        </div>

        {/* Inbetriebnahme-Protokoll */}
        <CommissioningProtocol token={token} />

        {/* PHOTO upload */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-3" data-testid="installer-photo-section">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Camera className="text-[#00E575]" size={20} /> Foto hochladen</h2>
          <div className="grid grid-cols-1 gap-3">
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Kategorie</span>
              <select className={inputCls} value={photoCategory} onChange={(e) => setPhotoCategory(e.target.value)} data-testid="photo-category">
                {info.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Beschreibung (optional)</span>
              <input className={inputCls} value={photoDescription} onChange={(e) => setPhotoDescription(e.target.value)} placeholder="z. B. Wandanschluss links neben Gastherme" data-testid="photo-description" />
            </label>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handlePhoto(e.target.files?.[0])}
              data-testid="photo-file-input"
            />
            <button
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="photo-upload-btn"
              className="h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-sm hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {uploadingPhoto ? <Loader2 size={18} className="animate-spin" /> : <><Camera size={18} /> Foto aufnehmen / auswählen</>}
            </button>
          </div>
        </section>

        {/* VIDEO upload */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-3" data-testid="installer-video-section">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Video className="text-[#00E575]" size={20} /> Video hochladen</h2>
          <div className="grid grid-cols-1 gap-3">
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Kategorie</span>
              <select className={inputCls} value={videoCategory} onChange={(e) => setVideoCategory(e.target.value)} data-testid="video-category">
                {info.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Beschreibung (optional)</span>
              <input className={inputCls} value={videoDescription} onChange={(e) => setVideoDescription(e.target.value)} placeholder="z. B. Hydraulik-Anbindung Detailaufnahme" data-testid="video-description" />
            </label>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleVideo(e.target.files?.[0])}
              data-testid="video-file-input"
            />
            <button
              onClick={() => videoInputRef.current?.click()}
              disabled={uploadingVideo}
              data-testid="video-upload-btn"
              className="h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-sm hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {uploadingVideo ? <Loader2 size={18} className="animate-spin" /> : <><Video size={18} /> Video aufnehmen / auswählen</>}
            </button>
            <p className="text-[12px] text-zinc-300 font-medium">Maximale Dateigröße: 200 MB</p>
          </div>
        </section>

        {/* MESSAGE */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-3" data-testid="installer-message-section">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Send className="text-[#00E575]" size={20} /> Nachricht an NOVATUS</h2>
          <div className="flex items-end gap-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="z. B. Wärmepumpe in Betrieb genommen, alle Werte im grünen Bereich. Kunde wurde eingewiesen."
              data-testid="installer-message-input"
              className="flex-1 resize-none bg-[#09090B] border border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-zinc-100 focus:border-[#00E575] outline-none"
            />
            <MicRecorder onTranscript={(t) => setMessage((prev) => (prev ? `${prev.trimEnd()} ${t}` : t))} />
          </div>
          <button
            onClick={sendMessage}
            disabled={sendingMsg || !message.trim()}
            data-testid="installer-message-send"
            className="h-11 px-5 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-sm hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center gap-2"
          >
            {sendingMsg ? <Loader2 size={16} className="animate-spin" /> : <><Send size={16} /> Nachricht senden</>}
          </button>
        </section>

        {/* History */}
        {(info.photos.length > 0 || info.videos.length > 0 || info.messages.length > 0) && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2"><CheckCircle2 className="text-[#00E575]" size={20} /> Bisher hochgeladen</h2>
            {info.photos.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-xs text-zinc-400 font-semibold inline-flex items-center gap-1.5"><ImageIcon size={12} /> Fotos ({info.photos.length})</div>
                <ul className="space-y-1">
                  {info.photos.map((p) => (
                    <li key={p.id} className="text-xs text-zinc-300 flex items-start gap-2">
                      <CheckCircle2 size={12} className="text-[#00E575] mt-0.5 flex-shrink-0" />
                      <span><b>{p.category}</b>{p.description ? ` · ${p.description}` : ""} <span className="text-zinc-500">· {fmt(p.uploaded_at)}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {info.videos.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-xs text-zinc-400 font-semibold inline-flex items-center gap-1.5"><Film size={12} /> Videos ({info.videos.length})</div>
                <ul className="space-y-1">
                  {info.videos.map((v) => (
                    <li key={v.id} className="text-xs text-zinc-300 flex items-start gap-2">
                      <CheckCircle2 size={12} className="text-[#00E575] mt-0.5 flex-shrink-0" />
                      <span><b>{v.category}</b>{v.description ? ` · ${v.description}` : ""} <span className="text-zinc-500">· {fmt(v.uploaded_at)}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {info.messages.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-xs text-zinc-400 font-semibold">Nachrichten ({info.messages.length})</div>
                <ul className="space-y-1.5">
                  {info.messages.map((m) => (
                    <li key={m.id} className="text-xs text-zinc-300 bg-zinc-900/50 rounded-lg px-2 py-1.5">
                      {m.text}
                      <div className="text-[12px] text-zinc-300 mt-0.5">{fmt(m.created_at)}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        <footer className="text-center text-[12px] text-zinc-400 py-4">
          NOVATUS Energie GmbH · Maximiliankorso 60 · 13465 Berlin · info@novatus-se.com
        </footer>
      </main>
    </div>
  );
}
